import { Divider, Grid, Theme, makeStyles } from '@material-ui/core';
import { colors, useControlledExpandBehavior } from 'design-system';
import { useMemo, useState } from 'react';

import { COMPLETE_VEHICLE } from 'data-layer-cr/dist/constants/products';
import { assignUid } from 'data-layer-cr/dist/functions/helpers';
import { maskAndSortUnits } from 'data-layer-cr/dist/functions/quotations';
import { Quotation } from 'data-layer-cr/dist/models';

import {
  ContractingInformationCard,
  IndividualInformation,
  VehicleInformationCard,
} from '../InformationCards';
import SumaryDialog from '../SumaryDialog';
import UnitSelectionItem from '../UnitSelectionItem';

const useStyles = makeStyles((theme: Theme) => ({
  messageStyle: {
    [theme.breakpoints.up('md')]: {
      textAlign: 'center',
    },
  },
  textStyle: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '14px',
    },
  },
  lightBlue: {
    color: colors.bright_turquoise,
  },
  buttonContainer: {
    width: 240,
    display: 'inline-block',
  },
  separator: {
    height: 1,
    backgroundColor: colors.gallery,
  },
}));

type IndividualSelectionProps = {
  quotation: Quotation;
  onUnitSelected: (packageId: number, forceNext: boolean) => void;
};

const IndividualSelection = ({
  quotation,
  onUnitSelected,
}: IndividualSelectionProps): JSX.Element => {
  const classes = useStyles();
  const [policySummaryVisible, togglePolicySummaryVisible] = useState(false);
  const { isExpanded, toggleExpandStatus } = useControlledExpandBehavior({
    defaultExpanded: '',
  });

  const unitList = useMemo(() => {
    return assignUid(
      quotation.productId === COMPLETE_VEHICLE
        ? maskAndSortUnits(quotation)
        : quotation.units,
    );
  }, [quotation]);

  return (
    <Grid container justifyContent="center">
      <Grid container spacing={3} item xs={12} md={12} lg={10} xl={8}>
        <IndividualInformation
          quotation={quotation}
          data-testid="individual-information-message"
        />
        {unitList.map((unitWithUniqueId): JSX.Element => {
          const onForceCollapse = (): void => {
            toggleExpandStatus(unitWithUniqueId.id);
          };
          return (
            <Grid item xs={12} md={4} key={unitWithUniqueId.id}>
              <UnitSelectionItem
                data-testid={`selection-item-${unitWithUniqueId.id}`}
                packageOption={unitWithUniqueId.data.package.id}
                unit={unitWithUniqueId.data}
                onSelected={onUnitSelected}
                forceCollapsed={!isExpanded(unitWithUniqueId.id)}
                onForceCollapsedUpdate={onForceCollapse}
              />
            </Grid>
          );
        })}

        <Grid item xs={12}>
          <Divider
            data-testid="section-divider"
            className={classes.separator}
          />
        </Grid>

        <Grid item xs={12}>
          <Grid item container spacing={3}>
            <Grid item xs={12} md={4}>
              <ContractingInformationCard
                data-testid="contracting-information-card"
                quotation={quotation}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <VehicleInformationCard
                data-testid="vehicle-information-card"
                quotation={quotation}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <SumaryDialog
        data-testid="summary-dialog-layout"
        open={policySummaryVisible}
        onCloseRequested={(): void =>
          togglePolicySummaryVisible(!policySummaryVisible)
        }
        optionSelected={quotation.units[0].package.id}
      />
    </Grid>
  );
};

export default IndividualSelection;
